/**
 * Reason codes are **namespaced by the thing that produces them**, and this
 * package deliberately does not enumerate them all.
 *
 * A flat global union would make `packages/contracts` a bottleneck every new
 * provider has to edit, and would make adding a provider a breaking change to
 * the OpenAPI document. The contract already requires apiClients to tolerate an
 * unknown code by falling back to `allowDeferred`, so enumerating them bought
 * nothing and cost extensibility.
 *
 *   core.quota_exhausted   engine-level outcomes, owned here
 *   mf.vat_removed         owned by the MF provider package
 *   krz.bankruptcy         owned by a provider that does not exist yet
 *
 * Codes are stable strings and part of the public contract: they end up in
 * audit records that outlive any refactor. Adding one is not a breaking change;
 * renaming or removing one is.
 */
const REASON_CODE_PATTERN = /^[a-z][a-z0-9]*\.[a-z][a-z0-9_]*$/;
export const REASON_CODE_REGEX = REASON_CODE_PATTERN.source;
export function isReasonCode(value) {
    return REASON_CODE_PATTERN.test(value);
}
export function reasonNamespace(code) {
    const [namespace] = code.split(".");
    return namespace ?? "";
}
/**
 * Declares the codes a provider or rule owns, with the namespace applied once.
 * The returned object is keyed by the bare name, so a provider refers to its
 * own codes without repeating its prefix.
 */
export function defineReasonCodes(namespace, names) {
    if (!/^[a-z][a-z0-9]*$/.test(namespace)) {
        throw new Error(`Invalid reason namespace "${namespace}": expected lowercase alphanumeric`);
    }
    const entries = names.map((name) => {
        const code = `${namespace}.${name}`;
        if (!isReasonCode(code)) {
            throw new Error(`Invalid reason code "${code}"`);
        }
        return [name, code];
    });
    return Object.freeze(Object.fromEntries(entries));
}
/**
 * Outcomes the engine itself produces, independent of any provider. Which
 * provider was stale or unavailable is recorded on the provider call, not
 * smuggled into the code.
 */
export const CoreReason = defineReasonCodes("core", [
    "nip_invalid",
    "no_applicable_rule",
    "quota_exhausted",
    "provider_unavailable",
    "manual_override",
    "rule_conditions_unmet",
]);
//# sourceMappingURL=reason-code.js.map